import axios from "../plugins/axios";
import { getField, updateField } from "vuex-map-fields";
import localService from "@/services/localService";
import { vm } from "@/main";
import moment from "moment";
import { getActiveQuotes, getInactiveQuotes, getActiveBoundPolicies } from "@/services/quoteFilters";
import {
  deriveQuickQuotesFlowActive,
  isReviewingQuotesStatus,
  deriveCustomerQuotesStoreBranch,
  deriveBrokerSidePanelDefaultTab,
  normalizeQuotesForDisplay,
} from "@/services/quoteNormalizationService";
import { DISABLE_LEGACY_MSHIFT_QUOTING } from "@/config/constants";

/**
 * Send to Broker succeeded but the application could not be synced to the
 * underwriters for a reason the company can fix, e.g. "Company Setup
 * questionnaire is missing" (S2CWB-333). Shown instead of failing silently.
 */
function showUwbSyncWarning(dispatch, data) {
  const warning = data && typeof data.uwbSyncWarning === "string" ? data.uwbSyncWarning.trim() : "";
  if (!warning || typeof dispatch !== "function") return;
  dispatch(
    "showMessage",
    { message: warning, messageType: "error", messageTitle: "Not sent to underwriters" },
    { root: true }
  );
}

export default {
  namespaced: true,
  state: () => ({
    quoteSectionTitle: "",
    brokerStatus: null,
    isLoadingBrokerStatus: false,
    originalData: [],
    carrierListNames: [],
    carrierList: [],
    carrierListSelection: [],
    companyData: null,
    quickQuoteListLoading: false,
    quickQuotes: null,
    readyToSubmit: false,
    quickQuotesSubmission: null,
    selectedQuoteForReview: null,
    reviewQuotesDialog: false,
    loadingQuoteDetailsForReview: false,
    selectedCarrierImageSrc: null,
    isQuickQuoteLoading: false,

    // FULL QUOTE
    fullQuotes: null,
    canSubmitFullQuotes: false,
    fullQuotesSubmission: null,
    selectedFullQuote: null,
    fullQuoteListLoading: false,
    isFullQuoteLoading: false,

    // BIND
    bindDialog: false,
    bindResponse: null,

    typeOfQuote: null,

    quoteReviewedAndSentToBroker: false,
  }),

  mutations: {
    updateField,

    setState(state, payload) {
      state[payload.key] = payload.value;
    },

    /**
     * Generic mutation to update a quote in the appropriate list
     * @param {Object} state - Vuex state
     * @param {Object} data - Quote data with quote_type field
     */
    updateQuote(state, data) {
      if (data.quote_type === "quick_quotes") {
        const index = state.quickQuotes?.quotes?.findIndex((e) => e.quoteAppId === data.quoteAppId);
        if (index > -1) {
          vm.$set(state.quickQuotes.quotes, index, data);
        }
      } else if (data.quote_type === "full_quotes") {
        const index = state.fullQuotes?.quotes?.findIndex((e) => e.quoteAppId === data.quoteAppId);
        if (index > -1) {
          vm.$set(state.fullQuotes.quotes, index, data);
        }
      }
    },

    /**
     * Generic mutation to set quote loading state
     * @param {Object} state - Vuex state
     * @param {Object} payload - { type: 'quick_quotes'|'full_quotes', loading: boolean }
     */
    setQuoteLoading(state, { type, loading }) {
      if (type === "quick_quotes") {
        state.quickQuoteListLoading = loading;
      } else if (type === "full_quotes") {
        state.fullQuoteListLoading = loading;
      }
    },

    updateQuoteDetail(state, data) {
      let index = null;

      if (data.quote_type == "quick_quotes") {
        index = state.quickQuotes?.quotes?.findIndex((e) => e.quoteAppId == data.quoteAppId);
        if (index > -1) {
          vm.$set(state.quickQuotes.quotes, index, data);
        }
      } else {
        index = state.fullQuotes?.quotes?.findIndex((e) => e.quoteAppId == data.quoteAppId);
        if (index > -1) {
          vm.$set(state.fullQuotes.quotes, index, data);
        }
      }
    },

    updateQuoteStatus(state, data) {
      // Pusher events may send only `quick_quotes_submission` without `quick_quotes`.
      // Fall back to current state so `readyToSubmit` stays accurate when quote rows already exist.
      const quickQuotesPayload =
        data && typeof data === "object" && "quick_quotes" in data ? data.quick_quotes : state.quickQuotes;
      const submissionPayload = data?.quick_quotes_submission;

      state.readyToSubmit = deriveQuickQuotesFlowActive({
        quick_quotes: quickQuotesPayload,
        quick_quotes_submission: submissionPayload,
      });
      state.quickQuotesSubmission = submissionPayload;
    },

    updateFullQuoteStatus(state, data) {
      state.canSubmitFullQuotes = data?.ready_for_full_quote;
      state.fullQuotesSubmission = data?.full_quotes_submission;
    },

    generateCarrierList(state, data) {
      let carrierList = Object.assign([], state.carrierList);
      data.map((carrierName) => {
        if (!carrierList.some((e) => e.name?.toLowerCase() == carrierName?.toLowerCase())) {
          carrierList.push({
            name: carrierName,
            selected: false,
          });
        }
      });
      state.carrierList = Object.assign([], carrierList);
    },

    updateCarrierState(state, payload) {
      let carrierList = Object.assign([], state.carrierList);
      if (!payload.isMma) {
        let chubbIndex = carrierList.findIndex((e) => e.name?.toLowerCase() == "chubb admitted");
        carrierList.splice(chubbIndex, 1);
      }
      let carrierFound = false;
      carrierList.map((data) => {
        carrierFound = false;
        if (data) {
          if (payload.carrierList) {
            if (payload.carrierList.some((e) => e.name?.toLowerCase() == data.name?.toLowerCase())) {
              carrierFound = true;
            }
            data.selected = payload.carrierList.find(
              (e) => e.name?.toLowerCase() == data.name?.toLowerCase()
            )?.selected;
          } else if (!payload.isMma) {
            data.selected = true;
          }
          if (!carrierFound && payload.extractedCarriers?.some((e) => e?.toLowerCase() == data.name?.toLowerCase())) {
            data.selected = true;
          }
        }
      });
      state.carrierList = Object.assign([], carrierList);
    },

    updateQuoteSignStatus(state, payload) {
      const fullQuotes = state.fullQuotes?.quotes || [];
      const quickQuotes = state.quickQuotes?.quotes || [];

      // Update sign_status in fullQuotes if it exists
      if (fullQuotes.length) {
        const fullQuoteIndex = fullQuotes.findIndex((quote) => quote.quoteAppId === payload.quote_app_id);
        if (fullQuoteIndex !== -1) {
          state.fullQuotes.quotes[fullQuoteIndex].sign_status = payload.sign_status;
        }
      }

      // Update sign_status in quickQuotes if it exists
      if (quickQuotes.length) {
        const quickQuoteIndex = quickQuotes.findIndex((quote) => quote.quoteAppId === payload.quote_app_id);
        if (quickQuoteIndex !== -1) {
          state.quickQuotes.quotes[quickQuoteIndex].sign_status = payload.sign_status;
        }
      }
    },

    updateQuoteSentToAgentStatus(state, payload) {
      const fullQuotes = state.fullQuotes?.quotes || [];
      const quickQuotes = state.quickQuotes?.quotes || [];

      // Update sign_status in fullQuotes if it exists
      if (fullQuotes.length) {
        const fullQuoteIndex = fullQuotes.findIndex((quote) => quote.quoteAppId === payload.quote_app_id);
        if (fullQuoteIndex !== -1) {
          state.fullQuotes.quotes[fullQuoteIndex].sent_to_agent = payload.sent_to_agent;
        }
      }

      // Update sign_status in quickQuotes if it exists
      if (quickQuotes.length) {
        const quickQuoteIndex = quickQuotes.findIndex((quote) => quote.quoteAppId === payload.quote_app_id);
        if (quickQuoteIndex !== -1) {
          state.quickQuotes.quotes[quickQuoteIndex].sent_to_agent = payload.sent_to_agent;
        }
      }
    },

    setQuickQuoteToGenerating(state) {
      state.readyToSubmit = false;
      state.quickQuotesSubmission = { status: "GeneratingQuotes" };
      state.quickQuotes = null;
    },

    setQuickQuoteToDefault(state) {
      state.quickQuotesSubmission = {};
      state.readyToSubmit = deriveQuickQuotesFlowActive({
        quick_quotes: state.quickQuotes,
      });
    },

    setFullQuoteToGenerating(state) {
      state.canSubmitFullQuotes = false;
      state.fullQuotesSubmission = { status: "GeneratingQuotes" };
      state.fullQuotes = null;
    },

    setFullQuoteToDefault(state) {
      state.canSubmitFullQuotes = true;
      state.fullQuotesSubmission = {};
    },
  },

  actions: {
    async saveCarrierList({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        const { data } = await axios.post(`/carrier_market`, { carriers: payload });
        if (!data.success) {
          return;
        }
        commit("setState", { key: "carrierListNames", value: data?.carrier_markets?.carriers || [] });
        commit("generateCarrierList", data?.carrier_markets?.carriers || []);
      } catch (error) {
        console.error(error);
      } finally {
        // no-op
      }
    },
    async getCarrierList({ commit }) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.get(`/carrier_market`);
        if (!data.success) {
          return;
        }
        commit("setState", { key: "carrierListNames", value: data?.carrier_markets?.carriers || [] });
        commit("generateCarrierList", data?.carrier_markets?.carriers || []);
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },
    async getMshiftBrokerStatus({ commit }) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("setState", { key: "isLoadingBrokerStatus", value: true });
        const { data } = await axios.get(`/mshift/broker_status`);
        if (!data.success) {
          return;
        }
        commit("setState", { key: "brokerStatus", value: data.broker_info });
      } catch (error) {
        console.error(error);
      } finally {
        commit("setState", { key: "isLoadingBrokerStatus", value: false });
      }
    },
    async getMShiftCarrierSettings({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.get(`/carrier_settings/${payload.organization_id}`);
        if (data.success) {
          let subdomain = null;
          let regexParse = /([a-z-0-9]{2,63}).([a-z.]{2,5})$/;
          let urlParts = regexParse.exec(globalThis.location.hostname);
          subdomain = globalThis.location.hostname.replaceAll(urlParts[1] + "." + urlParts[2], "").slice(0, -1);
          const isMma = subdomain.includes("mma") || subdomain.includes("readiness") || subdomain.includes("localhos");
          if (data.carrier_settings?.carriers) {
            commit("updateCarrierState", {
              isMma: isMma,
              carrierList: data.carrier_settings?.carriers,
              extractedCarriers: payload.extracted_carriers,
            });
          } else {
            commit("updateCarrierState", { isMma: isMma, extractedCarriers: payload.extracted_carriers });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async saveMShiftCarrierSettings({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post(`/carrier_settings/${payload.organization_id}`, payload);
        if (data.success) {
          commit("setState", { key: "carrierList", value: data.carrier_settings?.carriers });
        }
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getMShiftSubmittedQuotes({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.get(`/submission/quick_quotes/${payload.organization_id}`);
        if (!data.success) {
          return;
        }
        commit("updateQuoteStatus", data);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getMShiftQuotes({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        commit("setState", { key: "quickQuoteListLoading", value: true });
        const { data } = await axios.get(`/quick_quotes/${payload.organization_id}`);
        if (data.quick_quotes?.quotes && !payload.fromPusher) {
          await dispatch("getMShiftSubmittedQuotes", payload);
        }
        commit("setState", {
          key: "quickQuotes",
          value: normalizeQuotesForDisplay(data.quick_quotes || {}),
        });
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
        commit("setState", { key: "quickQuoteListLoading", value: false });
      }
    },

    async submitMShiftQuotes({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        commit("setQuickQuoteToGenerating");
        const { data } = await axios.post(`/submit/quick_quotes/${payload.organization_id}`);
        if (!data.success) {
          commit("setQuickQuoteToDefault");
          return;
        }
        commit("setState", {
          key: "quickQuotes",
          value: normalizeQuotesForDisplay(data.quick_quotes || data || {}),
        });
        commit("updateQuoteStatus", data);
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async reSubmitMShiftQuotes({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        commit("setQuickQuoteToGenerating");
        const { data } = await axios.post(`/resubmit/quick_quotes/${payload.organization_id}`);
        if (!data.success) {
          commit("setQuickQuoteToDefault");
          return;
        }
        commit("setState", {
          key: "quickQuotes",
          value: normalizeQuotesForDisplay(data.quick_quotes || data || {}),
        });
        commit("updateQuoteStatus", data);
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async submitFullQuotes({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        commit("setFullQuoteToGenerating");
        const { data } = await axios.post(`/submit/full_quotes/${payload.organization_id}`);
        if (!data.success) {
          commit("setFullQuoteToDefault");
          return;
        }
        commit("updateFullQuoteStatus", data);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async reSubmitFullQuotes({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        commit("setFullQuoteToGenerating");
        let { data } = await axios.post(`/resubmit/full_quotes/${payload.organization_id}`);
        if (!data.success) {
          commit("setFullQuoteToDefault");
          return;
        }
        commit("setState", {
          key: "fullQuotes",
          value: normalizeQuotesForDisplay(data.full_quotes || data || {}),
        });
        commit("updateFullQuoteStatus", data);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async submitFullQuoteForAdditionalInfo({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        const { data } = await axios.post(`/company_setup/${payload.organization_id}/request_additional_info`);
        if (!data.success) return;
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async notifyBrokerQuickQuotes({ commit, dispatch, rootState }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post(`/company_setup/brokers/notify_quick_quotes`, payload);
        if (!data.success) {
          return;
        }
        showUwbSyncWarning(dispatch, data);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async notifyBrokerFullQuotes({ commit, dispatch, rootState }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.post(`/company_setup/brokers/notify_full_quotes`, payload);
        if (!data.success) {
          return;
        }
        showUwbSyncWarning(dispatch, data);
        let currentUser = rootState.login.currentUser;
        currentUser.user.showQuickQuotes = false;
        commit("login/setState", { key: "currentUser", value: currentUser }, { root: true });
        localService.setItem("userInfo", currentUser);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getQuoteDetailsById({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return { success: false };
      try {
        commit("startLoading", null, { root: true });
        const { data } = await axios.get(`/quick_quotes/${payload.organization_id}/${payload.quote_app_id}`);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getFullQuotesSubmissionStatus({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.get(`/submission/full_quotes/${payload.organization_id}`);
        if (!data.success) return;
        commit("updateFullQuoteStatus", data);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getFullQuotes({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("setState", { key: "fullQuoteListLoading", value: true });
        let { data } = await axios.get(`/full_quotes/${payload.organization_id}`);
        if (!data.success) return;
        commit("setState", {
          key: "fullQuotes",
          value: normalizeQuotesForDisplay(data.full_quotes || {}),
        });
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
        commit("setState", { key: "fullQuoteListLoading", value: false });
      }
    },

    async getQuickQuoteDetails({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.get(`/quick_quotes/${payload.organization_id}/${payload.quote_app_id}`);
        if (!data.success) return;
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getFullQuoteDetails({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.get(`/full_quotes/${payload.organization_id}/${payload.quote_app_id}`);
        if (!data.success) return;
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    /**
     * Generic bind action that works for both quote types
     * @param {Object} payload - { organization_id, quote_app_id, quoteType: 'quick_quotes'|'full_quotes' }
     */
    async bindQuote({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      const { organization_id, quote_app_id, quoteType } = payload;
      const endpoint = quoteType === "quick_quotes" ? "quick_quotes" : "full_quotes";
      const detailKey = quoteType === "quick_quotes" ? "quick_quotes_detail" : "full_quotes_detail";

      try {
        let { data } = await axios.post(`/${endpoint}/bind/${organization_id}/${quote_app_id}`);
        if (data?.success) {
          let resData = { ...data[detailKey] };
          resData.quote_type = quoteType;
          commit("updateQuoteDetail", resData);
        }
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async bindQuickQuote({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.post(`/quick_quotes/bind/${payload.organization_id}/${payload.quote_app_id}`);
        if (data.success) {
          let resData = { ...data.quick_quotes_detail };
          resData.quote_type = "quick_quotes";
          commit("updateQuoteDetail", resData);
        }
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async bindFullQuote({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.post(`/full_quotes/bind/${payload.organization_id}/${payload.quote_app_id}`);
        if (data?.success) {
          let resData = data.full_quotes_detail;
          resData.quote_type = "full_quotes";
          commit("updateQuoteDetail", resData);
        }
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async extractedCarriersList({ commit, dispatch, state }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      let quotes = [];
      if (payload.type == "quick-quotes") {
        quotes = state.quickQuotes?.quotes;
      } else if (payload.type == "full-quotes") {
        quotes = state.fullQuotes?.quotes;
      }
      let extractedCarriers = [];
      if (quotes?.length) {
        let admittedStatus = null;
        let carrierName = null;
        let fullName = null;
        let foundCarrier = null;
        const organizationId = payload.organization_id;
        let carrierListForSave = [];
        quotes.map((quote) => {
          admittedStatus = quote.admittedInd ? "Admitted" : "Non-Admitted";
          carrierName = quote.payToCarrierName || "";
          fullName = `${carrierName} ${admittedStatus} ${quote.amBestRating || ""}`.trim();
          foundCarrier = state.carrierListNames.some((e) => e?.toLowerCase() == fullName?.toLowerCase());
          if (!foundCarrier) {
            carrierListForSave.push(fullName);
          }
          extractedCarriers.push(fullName);
        });

        if (carrierListForSave.length) {
          let updatedList = [...state.carrierListNames, ...carrierListForSave];
          await dispatch("saveCarrierList", updatedList).then(async () => {
            await dispatch("getMShiftCarrierSettings", {
              organization_id: organizationId,
              extracted_carriers: extractedCarriers,
            });
          });
        } else {
          await dispatch("getMShiftCarrierSettings", {
            organization_id: organizationId,
            extracted_carriers: extractedCarriers,
          });
        }
      }
    },

    async getQuotesByClient({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      const type = payload.type || "quick_quotes";
      const organizationId = payload.organization_id;
      commit("startLoading", null, { root: true });
      let loadingKey = type === "full_quotes" ? "isFullQuoteLoading" : "isQuickQuoteLoading";
      let dataKey = type === "full_quotes" ? "fullQuotes" : "quickQuotes";

      commit("setState", { key: loadingKey, value: true });
      try {
        let { data } = await axios.get(`/organization/quotes/${type}/${organizationId}`);
        if (data.success) {
          commit("setState", { key: loadingKey, value: false });
          const raw = data[type] || {};
          const value = normalizeQuotesForDisplay(raw);
          commit("setState", { key: dataKey, value });
          return data;
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async signQuote({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        let quoteAppId = payload.quote?.quoteAppId || payload.quote_app_id;
        const { data } = await axios.post(`/quote/sign/${payload.organization_id}/${quoteAppId}`);
        if (!data.success) return;
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async getBrokerSidePanelMShiftQuotesDetails({ commit }, orgId) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        commit("startLoading", null, { root: true });
        commit("setState", { key: "isQuickQuoteLoading", value: true });
        commit("setState", { key: "isFullQuoteLoading", value: true });
        const { data } = await axios.get(`/customer_details/quotes/${orgId}`);
        commit("updateQuoteStatus", data);
        commit("updateFullQuoteStatus", data);
        commit("setState", {
          key: "quickQuotes",
          value: normalizeQuotesForDisplay(data.quick_quotes || {}),
        });
        commit("setState", {
          key: "fullQuotes",
          value: normalizeQuotesForDisplay(data.full_quotes || {}),
        });
        commit("setState", { key: "isQuickQuoteLoading", value: false });
        commit("setState", { key: "isFullQuoteLoading", value: false });
        return data;
      } catch (error) {
        console.error(error);
        const empty = {
          quick_quotes: {},
          full_quotes: {},
          quick_quotes_submission: {},
          full_quotes_submission: {},
          ready_for_full_quote: false,
        };
        commit("updateQuoteStatus", empty);
        commit("updateFullQuoteStatus", empty);
        commit("setState", { key: "quickQuotes", value: normalizeQuotesForDisplay({}) });
        commit("setState", { key: "fullQuotes", value: normalizeQuotesForDisplay({}) });
        commit("setState", { key: "isQuickQuoteLoading", value: false });
        commit("setState", { key: "isFullQuoteLoading", value: false });
        return null;
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    /**
     * Trigger backend to poll UWB (or provider) for latest quotes and update DB.
     * Call this before loading quotes in the customer sidepanel when using polling instead of webhooks.
     * @param {Object} payload - { organizationId, quoteType } where quoteType is "quick_quotes" or "full_quotes"
     * @returns {Promise<Object|null>} API response or null on error
     */
    async refreshQuotesFromProvider({ commit }, { organizationId, quoteType }) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        const { data } = await axios.post(`/quotes/refresh/${organizationId}/${quoteType}`);
        return data;
      } catch (error) {
        // No submission or provider error - caller can still load whatever is in DB
        if (error?.response?.status === 400 || error?.response?.status === 404) {
          return null;
        }
        return null;
      }
    },

    async exportQuotes({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      let payloadData = { ...payload };
      let orgId = payloadData.organization_id;
      delete payloadData.organization_id;
      await axios
        .post(`quotes/export/quick_quotes/${orgId}`, payloadData, {
          responseType: "blob",
        })
        .then((response) => {
          let reader = new FileReader();
          reader.readAsDataURL(response.data);
          reader.onloadend = function () {
            const url = globalThis.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Export Quotes.csv"); // or any other extension
            document.body.appendChild(link);
            link.click();
          };
        });
    },

    async getCustomerQuotesByClient({ commit, dispatch }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      try {
        let { data } = await axios.get(`/customer/quotes/${payload.organization_id}`);
        if (!data.success) return;
        commit("setState", {
          key: "fullQuotes",
          value: normalizeQuotesForDisplay(data.full_quotes || {}),
        });
        commit("setState", {
          key: "quickQuotes",
          value: normalizeQuotesForDisplay(data.quick_quotes || {}),
        });
        commit("updateQuoteStatus", data);
        commit("updateFullQuoteStatus", data);
        return data;
      } catch (error) {
        console.error(error);
      } finally {
        commit("stopLoading", null, { root: true });
      }
    },

    async quoteSendToAgent({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      let sendEmail = payload.send_email || true;
      let { data } = await axios.post(`quote/send_to_agent/${payload.organization_id}/${payload.quote_app_id}`, {
        sent_email: sendEmail,
      });
      if (data?.success) {
        commit("updateQuoteSentToAgentStatus", data.quote_details);
      }
    },

    async quoteSpeakWithAgent({ commit }, payload) {
      if (DISABLE_LEGACY_MSHIFT_QUOTING) return;
      let { data } = await axios.post(`quote/speak_with_agent/${payload.quote_type}/${payload.organization_id}`);
      return data;
    },
  },

  getters: {
    getField,

    /**
     * Get active (quoted/ready) quick quotes
     */
    activeQuickQuotes: (state) => {
      return getActiveQuotes(state.quickQuotes?.quotes || []);
    },

    /**
     * Get inactive quick quotes
     */
    inactiveQuickQuotes: (state) => {
      return getInactiveQuotes(state.quickQuotes?.quotes || []);
    },

    /**
     * Get active (quoted/ready) full quotes
     */
    activeFullQuotes: (state) => {
      const quotes = state.fullQuotes?.quotes || [];
      const active = getActiveQuotes(quotes);
      // For full quotes, also include bound quotes
      const bound = quotes.filter((e) => e.payToCarrierQuoteStatus?.toLowerCase() === "bound");
      return [...active, ...bound];
    },

    /**
     * Get inactive full quotes
     */
    inactiveFullQuotes: (state) => {
      return getInactiveQuotes(state.fullQuotes?.quotes || []);
    },

    /**
     * Get all bound policies from both quote types
     */
    boundPolicies: (state) => {
      return getActiveBoundPolicies(state.quickQuotes, state.fullQuotes);
    },

    /**
     * Get bound policies organized by year (current and past)
     */
    boundPoliciesByYear: (state) => {
      const boundPolicies = getActiveBoundPolicies(state.quickQuotes, state.fullQuotes);
      const currentPolicies = {};
      const pastPolicies = {};

      // Define current years (this year and last year)
      const latestYears = new Set([moment().format("YYYY"), moment().subtract(1, "year").format("YYYY")]);

      for (const policy of boundPolicies) {
        const bindCreated = policy.bindCreated || policy.created;
        const year = moment(bindCreated).format("YYYY");

        if (latestYears.has(year)) {
          if (!currentPolicies[year]) {
            currentPolicies[year] = [];
          }
          currentPolicies[year].push(policy);
        } else {
          if (!pastPolicies[year]) {
            pastPolicies[year] = [];
          }
          pastPolicies[year].push(policy);
        }
      }

      // Sort past policy years in descending order
      const pastYears = Object.keys(pastPolicies).sort((a, b) => b - a);
      const sortedPastPolicies = {};
      for (const year of pastYears) {
        sortedPastPolicies[` ${year}`] = pastPolicies[year];
      }

      return {
        current: currentPolicies,
        past: sortedPastPolicies,
      };
    },

    /**
     * Check if any bound policies exist
     */
    hasBoundPolicies: (state, getters) => {
      const boundPolicies = getters.boundPoliciesByYear;
      const currentPolicies = boundPolicies.current;
      return Object.keys(currentPolicies).some((year) =>
        currentPolicies[year].some((policy) => {
          return (
            policy.payToCarrierQuoteStatus?.toLowerCase() === "bound" ||
            policy.bind_status?.toLowerCase() === "completed"
          );
        })
      );
    },

    /**
     * Check if a status string indicates reviewing quotes
     */
    isReviewingQuotes: () => (status) => {
      return isReviewingQuotesStatus(status);
    },

    /**
     * Check if quick quotes flow is active
     */
    isQuickQuotesFlowActive: (state) => {
      return deriveQuickQuotesFlowActive({
        quick_quotes: state.quickQuotes,
        quick_quotes_submission: state.quickQuotesSubmission,
      });
    },

    /**
     * Determine which quote branch to emphasize in customer view
     */
    customerStoreBranch: (state) => {
      return deriveCustomerQuotesStoreBranch({
        quick_quotes: state.quickQuotes,
        full_quotes: state.fullQuotes,
        quick_quotes_submission: state.quickQuotesSubmission,
        full_quotes_submission: state.fullQuotesSubmission,
        ready_for_full_quote: state.canSubmitFullQuotes,
      });
    },

    /**
     * Determine default tab for broker side panel
     */
    brokerPanelDefaultTab: (state) => {
      return deriveBrokerSidePanelDefaultTab({
        quick_quotes: state.quickQuotes,
        full_quotes: state.fullQuotes,
        quick_quotes_submission: state.quickQuotesSubmission,
        full_quotes_submission: state.fullQuotesSubmission,
        ready_for_full_quote: state.canSubmitFullQuotes,
      });
    },
  },
};
